import { useAdminStore } from '@admin/AppProvider'
import { Box, Container } from '@mui/material'
import { Loader, useMinimalsTheme } from '@tom-ui/ui'
import { appStore, PATHS, useAsyncFetch, useFormStore, withAuthenticationRequired } from '@tom-ui/utils'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useNavigate, useParams } from 'react-router'
import { UserFormHeader } from './components/molecules/UserFormHeader'
import { UserForm } from './components/organisms/UserForm'
import { UserFormMapper } from './helpers/user-form.mapper'
import { useUserMessageHandler } from './hooks/useUserMessageHandler'
import { UserFormProfile } from './models/user-form.profile'

export const UserFormPage = withAuthenticationRequired(
  observer(() => {
    const theme = useMinimalsTheme()
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { userStore, roleStore, authStore, tenantStore } = useAdminStore()
    const { getExceptionMessage, getSuccessMessage } = useUserMessageHandler()

    const formStore = useFormStore<UserFormProfile>()

    const userId = id && id !== 'new' ? id : null
    const isEdit = !!userId
    const canManageTenant = authStore.isSuperUser

    const { loading } = useAsyncFetch(async () => {
      const requests = [userStore.load(), roleStore.load()]
      if (canManageTenant) {
        requests.push(tenantStore.load())
      }

      if (isEdit) {
        requests.push(userStore.loadUserRoles(userId!))
      }

      await Promise.all(requests)
    }, [userStore, roleStore, tenantStore, isEdit, userId, canManageTenant])

    const selectedUser = computed(() =>
      isEdit ? userStore.data.find(x => x.user_id === userId) : undefined,
    ).get()

    const onSubmit = async () => {
      if (!formStore.validate()) return

      try {
        let upsertedUserId: string

        const tenantId = canManageTenant ? formStore.data.tenantId : authStore.tenantId

        if (isEdit) {
          upsertedUserId = userId!
          await userStore.update(
            UserFormMapper.mapFormValuesToUpdateDto({ ...formStore.data, id: userId, tenantId }),
          )
        } else {
          upsertedUserId = await userStore.add(
            UserFormMapper.mapFormValuesToCreateDto({ ...formStore.data, tenantId }),
          )
        }

        if (!formStore.data.isSuperUser)
          await userStore.updateUserRoles(
            upsertedUserId,
            formStore.data.roles.map(r => r.id),
          )

        appStore.setShowAlert('success', getSuccessMessage(isEdit))
        navigate(PATHS.config.users.url)
      } catch (error: any) {
        appStore.setShowAlert('error', getExceptionMessage(error))
      }
    }

    if (loading) return <Loader show />

    return (
      <Box sx={{ bgcolor: theme.palette.grey[100], minHeight: '100%' }}>
        <UserFormHeader isEdit={isEdit} onSubmit={onSubmit} />
        <Container sx={{ paddingY: theme.customSpacing.m }}>
          <UserForm
            formStore={formStore}
            user={selectedUser}
            roles={roleStore.data}
            userRoles={userStore.selectedUserRoles}
            tenants={tenantStore.sortedTenants}
            canManageTenant={canManageTenant}
          />
        </Container>
      </Box>
    )
  }),
)
